import { isNumber, uid } from 'radash';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';

import { Diagram, DiagramConnection, Zone, ZoneValidationError, ZoneValidationErrorType, ZoneValidator } from 'models';

// Unlike voltage, current is not shared across a zone, so both ends of each connection are compared instead
const checkCurrentLevels: ZoneValidator['validator'] = ({ diagram, zones }) => {
    const errors: ZoneValidationError[] = [];

    zones.forEach((zone) => {
        zone.connections.forEach((connection) => {
            const { from, to } = connection;

            checkNominalCurrentAgainstMaximum(errors, diagram, zone, connection, from, to);
            checkNominalCurrentAgainstMaximum(errors, diagram, zone, connection, to, from);
        });
    });

    return errors;
};

const checkNominalCurrentAgainstMaximum = (
    errors: ZoneValidationError[],
    diagram: Diagram,
    zone: Zone,
    connection: DiagramConnection,
    nominalEndpoint: DiagramConnection['from' | 'to'],
    maximumEndpoint: DiagramConnection['from' | 'to'],
) => {
    const nom = getEndpointCurrent(diagram, nominalEndpoint)?.nom;
    const max = getEndpointCurrent(diagram, maximumEndpoint)?.max;

    if (!isNumber(nom) || !isNumber(max) || nom <= max) return;

    const nominalComponentInstance = diagram.componentInstances[nominalEndpoint.componentInstanceId];
    const maximumComponentInstance = diagram.componentInstances[maximumEndpoint.componentInstanceId];

    errors.push({
        id: uid(6),
        type: ZoneValidationErrorType.CURRENT_NOMINAL_EXCEEDS_MAXIMUM,
        level: 'warning',
        payload: {
            zone,
            componentInstances: [nominalComponentInstance, maximumComponentInstance],
            connections: [connection],
            current: {
                nom,
                max,
                nomComponentInstances: [nominalComponentInstance],
                maxComponentInstances: [maximumComponentInstance],
            },
        },
    });
};

const getEndpointCurrent = (diagram: Diagram, endpoint: DiagramConnection['from' | 'to']) => {
    const { componentInstanceId: id, port: portIndex } = endpoint;

    if (portIndex === null) return;

    const componentInstance = diagram.componentInstances[id];

    // Parallel counts applied, so the array current is compared rather than a single module's
    return ComponentInstanceHelpers.normalise(componentInstance, portIndex)?.current;
};

export { checkCurrentLevels };
